/* eslint-disable camelcase */
import React from "react";
import Head from "next/head";
import getConfig from "next/config";
import cn from "classnames";
import { useDebounce } from "react-use";
import deepEqual from "deep-equal";
import { useRouter } from "next/router";
import { v4 as uuidv4 } from "uuid";

import { JobSearchHit, JobSearchLocation } from "@themuse/design-system";
import { triggerSnowplowSearchEvent, SnowplowSearchEventProps } from "@themuse/snowplow-js-client";

import { generateUrlPath } from "utils/browser";
import { fetchJobs } from "utils/api";
import { normalizeLocationName } from "utils/strings";
import { getRadioFacetValue } from "utils/searchFacets";
import { museTrack } from "utils/tracking";
import { getParams } from "utils/searchResults";
import { useSearchResultsState, useSearchResultsDispatch } from "hooks/SearchResultsContext";
import {
    UserInteractionActionTypes,
    SearchFilters,
    CompanyActionTypes,
    LocationActionTypes,
    KeywordActionTypes,
    FiltersActionTypes,
    UrlActionTypes,
    SearchIdActionTypes,
    UserInteractions,
    SearchDispatch,
} from "interfaces/context/searchContextInterfaces";
import { searchResultsSelector } from "selectors";
import { useSearchState, useSearchDispatch } from "hooks/SearchContext";
import { useWindowLocationState } from "hooks/WindowLocationContext";
import { useUserState } from "hooks/UserContext";
import { formatJobsResponse } from "orm/jobs";
import { buildSnowplowFilterItems } from "utils/searchFacetsTracking";
import SearchHeader from "./Header";
import { LoadMore } from "./LoadMore";
import { JobTile } from "./JobTile";
import { Empty } from "./Empty";

import styles from "./Results.module.scss";

interface ResultsProps {
    onClear?: () => void;
}

interface GetJobTileLocationProps {
    inputDma: string;
    locationType: string;
    locations: JobSearchLocation[];
    sessionDma: string;
}

/**
 * Extract the closest matching location out of a job and format it for display in a job tile.
 */
const getJobTileLocation = (props: GetJobTileLocationProps): string[] => {
    const {
        inputDma,
        locationType,
        locations,
        sessionDma
    } = props;
    const isRemote: boolean = locationType === "virtual";
    // We always prefer the dropdown if it was used.
    const userLocation: string = inputDma || sessionDma;
    // We Only care about the actual location address.
    const locationStrings: string[] = locations.map((location) => location.address);
    // This is the fully matched location (city and state).
    const matchedLocation: string = locationStrings.filter((loc) => loc === userLocation)[0];
    // We may need to match only on the last segment of the location.
    const getState = (txt: string) => {
        if (typeof txt === "string") {
            return txt.split(",")[1]?.trim()?.toLowerCase();
        }
        return null;
    };
    // First location matched by region.
    const matchedRegion: string = locationStrings.filter((address) => getState(address) === getState(userLocation))[0];
    // First location in the list (fall back)
    const firstLocation: string = locationStrings[0];
    // The location to display
    const displayLocation: string = matchedLocation || matchedRegion || firstLocation || "";
    // If we have more than 2 locations pluralize the string.
    const locationWord: string = ` location${locationStrings.length > 2 ? "s" : ""}`;
    // If we have more than 1 location we add a plus sign and the number plus the word location(s)
    const extraLocations: string = locationStrings.length > 1 ? ` + ${locationStrings.length - 1}${locationWord}` : "";
    // If we are remote and there is a location we will separate them with a slash.
    const separator: string = displayLocation ? " / " : "";
    // Build the remote string.
    const remoteString: string = isRemote ? `${separator}Remote` : "";

    // all together now...
    return [`${displayLocation}${remoteString}${extraLocations}`];
};

/**
 * Compiles the results tracking object.
 */
const setResultsTrackingObject = (
    count: number,
    keyword: string,
    userSelectedLocation: string,
    companyShortName: string,
    filters: SearchFilters,
    totalResultsOnPage: number,
    remoteOption: string
) => ({
    source: "search-listing",
    filter_source: "search-listing",
    total_results: count,
    keyword: keyword ?? "",
    search_location: userSelectedLocation ?? "",
    company_short_name: companyShortName ?? "",
    filters: {
        experience_level: filters?.experienceLevel ?? [],
        company_size: filters?.companySize ?? [],
        remote_work_location_type: remoteOption,
        industry: filters?.industry ?? [],
        perks_and_benefits: filters?.perks ?? [],
        job_categories: filters?.category ?? [],
        company_leadership: filters?.companyLeadership ?? [],
        radius: userSelectedLocation ? getRadioFacetValue("radius", filters?.radius ?? "") : "",
        date_posted: getRadioFacetValue("postedDateRange", filters?.postedDateRange ?? "")
    },
    total_results_on_page: totalResultsOnPage
});

/**
 * Renders a list of JobTile components based on the search results passed.
 */
const Tiles = (props: {
    searchResults: JobSearchHit[],
    locationName: string,
    userInteractions: UserInteractions,
    userLocationDma: string,
    resultsLength: number,
    searchDispatch: SearchDispatch
}) => {
    const {
        searchResults,
        locationName,
        userInteractions,
        userLocationDma,
        resultsLength,
        searchDispatch
    } = props;
    return (
        <>
            {searchResults.map((job: JobSearchHit, index) => {
                const inputDMA = locationName ?? "";
                const viewed = userInteractions.tilesViewedJobIds.includes(job.hit.id);
                const show_logo: boolean = !(["archived", "lite"].includes(job?.hit?.company?.client_tier ?? ""));

                const handleViewedCallback = (jobId: string) => {
                    searchDispatch({
                        type: UserInteractionActionTypes.updateJobTileViews,
                        payload: {
                            tilesViewedJobIds: [jobId]
                        }
                    });
                };

                return (
                    <JobTile
                        key={`${job.hit.id}`}
                        {...{
                            job,
                            index,
                            inputDMA,
                            userLocationDma,
                            total: resultsLength,
                            viewed,
                            showLogo: show_logo,
                            viewedCallback: () => handleViewedCallback(job.hit.id)
                        }}
                    />
                );
            })}
        </>
    );
};
Tiles.displayName = "Tiles";

/**
 * Render job search results and handling user interactions with the search results page.
 */
const Results = (props: ResultsProps) => {
    const { onClear } = props;
    const { publicRuntimeConfig: { museWebHost } } = getConfig();
    const { location: userLocation } = useUserState();
    const { href, search } = useWindowLocationState();
    const searchDispatch = useSearchDispatch();
    const searchResultsState = useSearchResultsState();
    const { totalCount, results } = searchResultsState;
    const searchResultsDispatch = useSearchResultsDispatch();
    const { ctsEnabled } = useSearchState();
    const [jobsHasBeenRequested, setJobsHasBeenRequested] = React.useState(false);
    /* eslint-disable-next-line no-unused-vars */
    const [userLocationDma, setUserLocationDma] = React.useState("");
    const [hasMore, setHasMore] = React.useState(!!(totalCount - results.length));
    const [classState, setClassState] = React.useState(styles.resultsGrid);
    const [initialLoad, setInitialLoad] = React.useState(true);
    const searchState = useSearchState();
    const {
        company,
        location,
        keyword,
        filters,
        locationName,
        latitude,
        latlng,
        remoteOption,
        companyName,
        companyShortName,
        category,
        searchKeyword,
        userInteractions,
        url,
        preference,
        searchId
    } = searchResultsSelector(searchState, userLocation);

    const [searchRequestState, setSearchRequestState] = React.useState({
        filters,
        companyShortName,
        searchKeyword,
        remoteOption,
        latlng,
        searchId
    });

    useDebounce(() => {
        // Handle any updated search params
        const updatedSearchRequestState = {
            filters,
            companyShortName,
            searchKeyword,
            remoteOption,
            latlng,
            searchId
        };

        if (!deepEqual(updatedSearchRequestState, searchRequestState)) {
            setSearchRequestState(updatedSearchRequestState);
        }
    }, 500, [
        company,
        location,
        keyword,
        filters
    ]);

    const router = useRouter() || { asPath: "" };
    const { asPath } = router;

    // This is the search id we want to use for tracking
    let uniqueSearchId = searchId;

    React.useEffect(() => {
        if (!(initialLoad && !!searchId)) {
            uniqueSearchId = uuidv4();
            searchDispatch({
                type: SearchIdActionTypes.updateSearchId,
                payload: uniqueSearchId
            });
        }
    }, [searchRequestState]);

    React.useEffect(() => {
        // We load the first set of search results server
        // side, so we don't want to fetch on initial load.
        if (!initialLoad) {
            const urlPath = generateUrlPath({
                companyShortName,
                locationName: normalizeLocationName(locationName),
                searchKeyword,
                filters,
                asPath,
            });
            fetchJobSearchResults();
            searchDispatch({
                type: UrlActionTypes.updateUrl,
                payload: urlPath
            });
        }
        museTrack("Search Query Submitted", {
            search_origin: href,
            search_term: searchKeyword ?? "",
            search_location: locationName,
            search_company: companyName ?? "",
            search_company_short_name: companyShortName
        });
        const searchEventProps: SnowplowSearchEventProps = {
            sp_search_type: "job",
            sp_search_id: uniqueSearchId,
            sp_search_keyword: searchKeyword
        };
        const isRemote = remoteOption === "virtual";
        const snowplowFilters = buildSnowplowFilterItems(filters, locationName, companyShortName);
        triggerSnowplowSearchEvent(searchEventProps, snowplowFilters, isRemote);
    }, [
        searchRequestState,
        userInteractions?.searchButtonClickedAt
    ]);

    React.useEffect(() => {
        if (initialLoad) {
            setInitialLoad(false);
            return;
        }

        museTrack("Search Results Returned", setResultsTrackingObject(
            searchResultsState.totalCount,
            searchKeyword,
            locationName,
            companyShortName,
            filters,
            searchResultsState.results.length,
            remoteOption
        ));
    }, [searchResultsState]);

    /**
     * Fetch jobs with an optional cursor Id.
     *
     * @param cursor This is the cursor within the last job on the page.
     * @returns Promise
     */
    const fetchJobSearchResults = async (cursor?: string): Promise<void> => {
        setJobsHasBeenRequested(false);

        // if this is not a load more request and we are scrolled at all, snap back to the top.
        // TODO: There are no tests for this as JSDom does not expose scrollTo nor does it measure the physical DOM.
        if (!cursor && window.scrollY) {
            window.scrollTo(0, 0);
        }

        const queryParams = getParams({
            filters,
            companyShortName,
            searchKeyword,
            remoteOption,
            latlng,
            cursor,
            searchApiPreference: preference
        });

        const jobsResponse = await fetchJobs(queryParams, ctsEnabled);
        const dispatchValues = formatJobsResponse(jobsResponse);
        const { count, hits } = jobsResponse;
        searchResultsDispatch({
            type: "update_results",
            payload: {
                values: {
                    ...dispatchValues,
                    results: cursor ? [...searchResultsState.results, ...hits] : [...hits]
                }
            }
        });
        setJobsHasBeenRequested(true);
        setHasMore(count - hits.length > 0);
    };

    /**
     * Clear all the search filters
     */
    const clearSearch = () => {
        searchDispatch({
            type: FiltersActionTypes.resetAllFilters
        });
        searchDispatch({
            type: CompanyActionTypes.resetCompany
        });
        searchDispatch({
            type: LocationActionTypes.resetLocation
        });
        searchDispatch({
            type: KeywordActionTypes.resetKeyword
        });
        onClear?.();
    };

    /**
     * This is the event fired by the load more button.
     * @param event:  The click event.
     */
    const handleLoadMoreClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
        museTrack("Pagination Link Clicked", {
            source: "search-listings",
            name: "load_more_results"
        });

        if (event) {
            event.preventDefault();
        }

        fetchJobSearchResults(searchResultsState.cursor);
    };

    /**
     * setUserLocationDma are triggered when any of the following are modified:
     * userInputs location lat changes
     */
    React.useEffect(() => {
        setUserLocationDma(userLocation?.dma);
    }, [latitude]);

    /**
     * Update the url
     */
    React.useEffect(() => {
        if (typeof url !== "undefined") {
            window.history.replaceState(null, null, `${url}${search}`);
        }
    }, [url]);

    React.useEffect(() => {
        const classes = cn(hasMore ? styles.hasFade : false, styles.resultsGrid);
        setClassState(cn(jobsHasBeenRequested || initialLoad ? null : styles.shimmer, classes));
    }, [hasMore, jobsHasBeenRequested]);

    const searchResults = searchResultsState?.results ?? [];
    const resultsLength = searchResults.length ?? 0;

    const getLDJson = (list: JobSearchHit[]) => {
        const itemListElement = list.map((job: JobSearchHit, index: number) => {
            const { apply_type, short_title } = job?.hit ?? {};
            const extension = apply_type === "external" ? "" : "#apply-inline-form";
            return {
                "@type": "ListItem",
                position: index + 1,

                url: `${museWebHost}/jobs/${job?.hit?.company?.short_name}/${short_title}${extension}`
            };
        });

        return JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement
        });
    };

    const tilesProps = {
        searchResults,
        locationName,
        userInteractions,
        userLocationDma,
        resultsLength,
        searchDispatch
    };

    return (
        <>
            <SearchHeader
                state={searchResultsState}
                location={locationName ?? null}
                keyword={searchKeyword ?? null}
                company={companyName ?? null}
                category={category}
            />
            <Head>
                <script
                    // using old style script and head so that this script will update on filter change.
                    type="application/ld+json"
                    id="ItemList"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: getLDJson(searchResults)
                    }}
                />
            </Head>
            {resultsLength
                ? (
                    <div className={classState}>
                        <Tiles {...tilesProps} />
                        <LoadMore shouldLoad={hasMore} cb={handleLoadMoreClick} />
                    </div>
                )
                : <Empty requested={jobsHasBeenRequested} results={resultsLength} cb={clearSearch} />}
        </>
    );
};

Results.displayName = "Results";
export default Results;
export {
    getJobTileLocation,
    LoadMore
};
export type { GetJobTileLocationProps };
